import React, { useContext, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import styled, { ThemeContext } from 'styled-components';
import { t } from 'ttag';
import config from '../../../../config/channels';
import { TDeliveryVenue } from '../../../../contexts/delivery-venue/delivery-venue-context';
import { TChannel } from '../../../../contexts/operator/operator-context';
import useOperatorContext from '../../../../contexts/operator/useOperatorContext';
import { TSite } from '../../../../contexts/site/site-context';
import NavPill from '../../../general/NavPill';
import { trackSiteSelectTab } from '../../../../services/siteSelectTracking';
import DeliveryPanel from './DeliveryPanel';
import SitesPanel from './SitesPanel';
import VenuesPanel from './VenuesPanel';

type SiteSelectModalProps = {
    // the channel in the link, if there is one: the tab to start on (or, for dine in, the only one)
    channel?: TChannel | null;
    enabledChannels: TChannel[];
    sites: TSite[];
    deliveryVenues: TDeliveryVenue[];
    // called as customers choose a site to collect from
    onSelectSite: (site: TSite) => void;
};

const MODAL_ID = 'site-select-modal';

// Where to order from, over the menu: the landing page's choice of channel, and of site or address within it.
// It stays until a choice is made, as there's nothing to order from before that.
const SiteSelectModal = ({ channel, enabledChannels, sites, deliveryVenues, onSelectSite }: SiteSelectModalProps) => {
    const operatorContext = useOperatorContext();
    const themeContext = useContext(ThemeContext);

    const first = channel && enabledChannels.includes(channel) ? channel : enabledChannels[0];
    const [active, setActive] = useState<TChannel | undefined>(first);
    // counts the clicks on the tabs, for the panel to put the cursor in its text box as each comes
    const [focusRequest, setFocusRequest] = useState(0);

    useEffect(() => {
        setActive(first);
    }, [first]);

    const selectTab = (item: TChannel) => {
        trackSiteSelectTab('modal', item);
        setActive(item);
        setFocusRequest((count) => count + 1);
    };

    const displayName = (channel: TChannel) => {
        if (channel === 'catering') {
            return operatorContext.operator?.theme.v4.catering_label ?? 'Catering';
        }

        return config[channel].displayName;
    };

    const renderPanel = () => {
        switch (active) {
            case 'collection':
                return (
                    <SitesPanel
                        key="collection"
                        channel="collection"
                        sites={sites.filter((site) => site.channels.collection)}
                        onSelect={onSelectSite}
                        focusRequest={focusRequest}
                    />
                );

            case 'dine_in':
                return (
                    <SitesPanel
                        key="dine_in"
                        channel="dine_in"
                        sites={sites.filter((site) => site.channels.dine_in)}
                        focusRequest={focusRequest}
                    />
                );

            case 'delivery':
            case 'catering':
                return <DeliveryPanel key={active} channel={active} focusRequest={focusRequest} />;

            case 'venues':
                return <VenuesPanel key="venues" venues={deliveryVenues} focusRequest={focusRequest} />;

            default:
                return <Empty>{t`No channel selected.`}</Empty>;
        }
    };

    return (
        <StyledModal
            id={MODAL_ID}
            show
            backdrop="static"
            keyboard={false}
            centered
            animation={false}
            aria-label={t`Order details`}
            // (the app's modals start out of sight, and come in once they have this class)
            onShow={() => document.getElementById(MODAL_ID)?.classList.add('anim-enter')}
        >
            { operatorContext.operator?.theme?.v4?.logo && (
                <Brand>
                    <span className="logo" role="img" aria-label={operatorContext.operator?.name ?? ''} />
                </Brand>
            )}

            <Head>
                { active && (
                    <>
                        <Tabs
                            className={`channel-tabs${enabledChannels.length === 1 ? ' solo' : ''}`}
                            role="group"
                            aria-label={t`How you’re ordering`}
                        >
                            <div>
                                { enabledChannels.map((item) => (
                                    <NavPill
                                        size="md"
                                        key={item}
                                        variant={themeContext.v4.navigation_style}
                                        className={active === item ? 'active' : undefined}
                                    >
                                        <button type="button" aria-pressed={active === item} onClick={() => selectTab(item)}>
                                            <h3>
                                                <span>{displayName(item)}</span>
                                            </h3>
                                        </button>
                                    </NavPill>
                                ))}
                            </div>
                        </Tabs>
                        <Fade />
                    </>
                )}
            </Head>

            {renderPanel()}
        </StyledModal>
    );
};

// The app's modals are a card in the middle of the screen, and a sheet from the bottom on phones
const StyledModal = styled(Modal)`
    .modal-dialog .modal-content {
        height: min(640px, calc(100dvh - 48px));
        align-self: center;
        background: var(--layouts_container_bg);
        color: var(--text_body_color);
        box-shadow: 0 20px 50px rgb(0 0 0 / 25%);

        @media screen and (max-width: 767px) {
            align-self: auto;
            height: 90dvh;
            border-radius: var(--radius_xl) var(--radius_xl) 0 0;
        }
    }
`;

const Brand = styled.div`
    flex: none;
    display: flex;
    align-items: center;
    height: 64px;
    padding: 0 24px;
    background: var(--layouts_header_bg);

    .logo {
        display: block;
        width: 100%;
        height: var(--logo_height);
        background-image: var(--logo);
        background-size: contain;
        background-position: center left;
        background-repeat: no-repeat;
    }

    @media screen and (max-width: 767px) {
        padding: 0 16px;
    }
`;

// Drawn as the menu's navigation is: its background and border, and the same pills (or tabs) on it.
const Head = styled.div`
    position: relative;
    flex: none;
    display: flex;
    align-items: stretch;
    background-color: var(--layouts_nav_bg);
    border-bottom: var(--layouts_nav_border);
    box-shadow: 0 1px 4px rgb(0 0 0 / 8%);
`;

// The tabs scroll sideways where there are too many, which clips anything outside them. So they take
// the whole of the header, padding included, and a tab styled to move on hover has the room to.
const Tabs = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    overflow-x: scroll;
    scrollbar-width: none;
    padding: 0 24px;

    &::-webkit-scrollbar {
        display: none;
    }

    @media screen and (max-width: 767px) {
        padding: 0 16px;
    }

    > div {
        display: flex;
    }
`;

// ...and fade out at the right, as the menu's do
const Fade = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 32px;
    pointer-events: none;
    background: linear-gradient(90deg, var(--layouts_nav_bg_opa0), var(--layouts_nav_bg));
`;

const Empty = styled.p`
    padding: 20px 24px;
`;

export default SiteSelectModal;
